import React from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import { ArrowForward } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

// Styled Components
const FreeTrialContainer = styled.div`
  background: #ffffff;
  padding: 6rem 2rem;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: url('data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 20"><defs><pattern id="grain" width="100" height="20" patternUnits="userSpaceOnUse"><circle cx="10" cy="10" r="1" fill="rgba(0,0,0,0.02)"/><circle cx="30" cy="5" r="1" fill="rgba(0,0,0,0.01)"/><circle cx="50" cy="15" r="1" fill="rgba(0,0,0,0.015)"/><circle cx="70" cy="8" r="1" fill="rgba(0,0,0,0.008)"/><circle cx="90" cy="12" r="1" fill="rgba(0,0,0,0.02)"/></pattern></defs><rect width="100" height="20" fill="url(%23grain)"/></svg>') repeat;
    pointer-events: none;
  }

  @media screen and (max-width: 768px) {
    padding: 4rem 1rem;
    background: #ffffff;
  }
`;

const TrialContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  position: relative;
  z-index: 1;

  @media screen and (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 3rem;
    text-align: center;
  }
`;

const TrialContent = styled(motion.div)`
  color: #1a202c;
`;

const TrialTitle = styled.h1`
  font-size: 3.5rem;
  font-weight: 800;
  line-height: 1.1;
  margin-bottom: 1.5rem;
  color: #1a202c;

  @media screen and (max-width: 768px) {
    font-size: 2.5rem;
  }

  @media screen and (max-width: 375px) {
    font-size: 2rem;
  }
`;

const Highlight = styled.span`
  background: linear-gradient(to right, #DD8FF9 0%, #A975FE 33%, #4F46E5 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const TrialDescription = styled.p`
  font-size: 1.2rem;
  line-height: 1.6;
  margin-bottom: 2.5rem;
  color: #4a5568;

  @media screen and (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const TrialActions = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 2rem;
  flex-wrap: wrap;

  @media screen and (max-width: 768px) {
    justify-content: center;
    flex-direction: column;
    align-items: center;
  }
`;

const TrialButton = styled(motion.button)`
  padding: 1rem 2rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  min-width: 160px;
  background: linear-gradient(135deg, #4F46E5 0%, #7C3AED 100%);
  color: white;
  box-shadow: 0 4px 15px rgba(79, 70, 229, 0.4);

  @media screen and (max-width: 768px) {
    width: 100%;
    max-width: 280px;
  }
`;

const TrialGuarantee = styled.div`
  color: #718096;
  font-size: 0.9rem;

  strong {
    color: #2d3748;
  }
`;

const TrialVisual = styled.div`
  position: relative;
  height: 400px;
  display: flex;
  align-items: center;
  justify-content: center;

  @media screen and (max-width: 768px) {
    height: 300px;
    order: -1;
  }
`;

const FloatingCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  width: 300px;
  position: relative;
  z-index: 2;

  @media screen and (max-width: 768px) {
    width: 260px;
    padding: 1.25rem;
  }

  @media screen and (max-width: 375px) {
    width: 240px;
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const CardDots = styled.div`
  display: flex;
  gap: 0.5rem;

  span {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background: #6b7280;

    &:nth-child(1) { background: #ef4444; }
    &:nth-child(2) { background: #f59e0b; }
    &:nth-child(3) { background: #10b981; }
  }
`;

const CardTitle = styled.span`
  font-weight: 600;
  color: #374151;
  font-size: 0.9rem;
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: #e5e7eb;
  border-radius: 4px;
  margin-bottom: 1.5rem;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: linear-gradient(90deg, #4F46E5, #7C3AED);
  width: 75%;
  border-radius: 4px;
  animation: progressAnimation 3s ease-in-out infinite;

  @keyframes progressAnimation {
    0%, 100% { width: 75%; }
    50% { width: 85%; }
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
`;

const StatItem = styled.div`
  text-align: center;
  padding: 1rem;
  background: #f9fafb;
  border-radius: 12px;
`;

const StatNumber = styled.span`
  display: block;
  font-size: 1.5rem;
  font-weight: 700;
  color: #4F46E5;
`;

const StatLabel = styled.span`
  font-size: 0.8rem;
  color: #6b7280;
  font-weight: 500;
`;

const FloatingNotification = styled(motion.div)`
  position: absolute;
  top: 20px;
  right: -20px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
  padding: 1rem 1.25rem;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  z-index: 3;

  @media screen and (max-width: 768px) {
    top: 10px;
    right: -10px;
    padding: 0.75rem 1rem;
  }
`;

const NotificationIcon = styled.div`
  width: 30px;
  height: 30px;
  background: linear-gradient(135deg, #10b981, #059669);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: bold;
  font-size: 0.8rem;
`;

const NotificationText = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    color: #374151;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }

  span {
    color: #6b7280;
    font-size: 0.8rem;
  }
`;

interface FreeTrialSectionProps {
  className?: string;
}

const FreeTrialSection: React.FC<FreeTrialSectionProps> = ({ className }) => {
  const navigate = useNavigate();

  return (
    <FreeTrialContainer className={className}>
      <TrialContainer>
        <TrialContent
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: "easeOut" }}
          viewport={{ once: true }}
        >
          <TrialTitle>
            Start Your <Highlight>Free Trial</Highlight> Today!
          </TrialTitle>
          
          <TrialDescription>
            Join thousands of service professionals who've transformed their business with ServTrackr. 
            Get full access to all features for 14 days—completely free.
          </TrialDescription>
          
          <TrialActions>
            <TrialButton
              onClick={() => navigate('/free-trial')}
              whileHover={{ scale: 1.05, boxShadow: "0 10px 25px rgba(79, 70, 229, 0.3)" }}
              whileTap={{ scale: 0.98 }}
            >
              Start Free Trial
              <ArrowForward style={{ marginLeft: '8px', fontSize: '20px' }} />
            </TrialButton>
          </TrialActions>
          
          <TrialGuarantee>
            <p>
              <strong>14-day free trial</strong> • <strong>Cancel anytime</strong> • <strong>Setup in 5 minutes</strong>
            </p>
          </TrialGuarantee>
        </TrialContent>
        
        <TrialVisual>
          <FloatingCard
            animate={{
              y: [0, -10, 0],
              rotate: [0, 1, 0]
            }}
            transition={{
              duration: 4,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          >
            <CardHeader>
              <CardDots>
                <span></span>
                <span></span>
                <span></span>
              </CardDots>
              <CardTitle>ServTrackr Dashboard</CardTitle>
            </CardHeader>
            <div>
              <ProgressBar>
                <ProgressFill />
              </ProgressBar>
              <StatsGrid>
                <StatItem>
                  <StatNumber>24</StatNumber>
                  <StatLabel>Active Jobs</StatLabel>
                </StatItem>
                <StatItem>
                  <StatNumber>12</StatNumber>
                  <StatLabel>Team Members</StatLabel>
                </StatItem>
              </StatsGrid>
            </div>
          </FloatingCard>
          
          <FloatingNotification
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ delay: 1, duration: 0.6 }}
            viewport={{ once: true }}
          >
            <NotificationIcon>✓</NotificationIcon>
            <NotificationText>
              <strong>Job Completed</strong>
              <span>HVAC Maintenance - $350</span>
            </NotificationText>
          </FloatingNotification>
        </TrialVisual>
      </TrialContainer>
    </FreeTrialContainer>
  );
};

export default FreeTrialSection;


